import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { useCurrency } from '../../context/CurrencyContext';
import { formatPrice } from '../../propertyUtils';
import { API_BASE } from '../../config';
import { trackEvent } from '../../analyticsLoader';
import { Tooltip, Modal, Select, Button, Alert } from '../ComponentLibrary';
import { AlertCircleIcon, ThumbsUpIcon, ReplyIcon, UserIcon } from '../IconSet';
import ReviewPhotoLightbox from '../ReviewPhotoLightbox';
import { REVIEW_TYPE_OPTIONS } from '../PropertyReviewForm';
import './SellerProfile-Simple.css';
import { MessageSquare, Flag, Star, CheckCircle, Heart } from 'lucide-react';
import sellerProfileMinimalAvatar from './seller-profile-minimal.svg';
import { blogCategoryLabel } from '../../config/blogCategories';

const SellerProfileFull = ({ seller = {} }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, authedFetch } = useAuth();
  const currencyCtx = useCurrency();
  const [activeTab, setActiveTab] = useState('properties');
  const [currentPage, setCurrentPage] = useState(1);
  const [isFavorite, setIsFavorite] = useState(Boolean(seller.isSavedByViewer));
  const [favoriteSubmitting, setFavoriteSubmitting] = useState(false);
  const [savedCount, setSavedCount] = useState(Number(seller.stats?.saved) || 0);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('Suspicious activity');
  const [reportDescription, setReportDescription] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportSubmitted, setReportSubmitted] = useState(Boolean(seller.isReportedByViewer));
  const { showToast: addToast } = useToast();
  const [sellerReviews, setSellerReviews] = useState(seller.sellerReviews || seller.reviews || []);
  const [responseDrafts, setResponseDrafts] = useState({});
  const [responseSubmittingId, setResponseSubmittingId] = useState(null);
  const [respondingReviewId, setRespondingReviewId] = useState(null);
  const [helpfulPendingReviewId, setHelpfulPendingReviewId] = useState(null);
  const [reportingReview, setReportingReview] = useState(null);
  const [reviewReportReason, setReviewReportReason] = useState('spam');
  const [reviewReportDetails, setReviewReportDetails] = useState('');
  const [reviewReportStatus, setReviewReportStatus] = useState('');
  const [reviewReportLoading, setReviewReportLoading] = useState(false);
  const REVIEWS_PAGE_SIZE = 2;
  const [visibleReviewCount, setVisibleReviewCount] = useState(REVIEWS_PAGE_SIZE);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const appendPropertyContext = (nextParams) => {
    const sourceParams = new URLSearchParams(location.search || '');
    const propertyId = sourceParams.get('propertyId');
    const propertyTitle = sourceParams.get('propertyTitle');
    const propertyPrice = sourceParams.get('propertyPrice');

    if (propertyId) {
      nextParams.set('propertyId', propertyId);
    }
    if (propertyTitle) {
      nextParams.set('propertyTitle', propertyTitle);
    }
    if (propertyPrice) {
      nextParams.set('propertyPrice', propertyPrice);
    }
  };

  // Pagination: 3x3 grid (9 per page)
  const propertiesPerPage = 9;

  const defaultSeller = {
    id: 1,
    name: 'Premium Properties',
    avatar: sellerProfileMinimalAvatar,
    rating: 4.8,
    reviewCount: 156,
    location: 'Lagos, Nigeria',
    joinDate: 'Since 2020',
    verified: true,
    bio: 'Premium real estate professional',
    stats: {
      propertiesListed: 62,
      sold: 25,
      responseTime: '< 2 hours',
      saved: 0,
    },
    contact: {
      phone: '+234 701 234 5678',
      email: 'info@properties.com',
    },
    properties: [
      {
        id: 1,
        title: 'Luxury Apartment in Lekki',
        price: 85000000,
        image: 'https://via.placeholder.com/300x200?text=Apt+1',
        beds: 3,
        baths: 2,
      },
      {
        id: 2,
        title: 'Modern Villa in Ikoyi',
        price: 120000000,
        image: 'https://via.placeholder.com/300x200?text=Villa+2',
        beds: 4,
        baths: 3,
      },
      {
        id: 3,
        title: 'Cozy Studio Apartment',
        price: 45000000,
        image: 'https://via.placeholder.com/300x200?text=Studio+3',
        beds: 1,
        baths: 1,
      },
      {
        id: 4,
        title: 'Spacious Family Home',
        price: 150000000,
        image: 'https://via.placeholder.com/300x200?text=Home+4',
        beds: 5,
        baths: 4,
      },
      {
        id: 5,
        title: 'Elegant Penthouse',
        price: 200000000,
        image: 'https://via.placeholder.com/300x200?text=Penthouse+5',
        beds: 3,
        baths: 3,
      },
      {
        id: 6,
        title: 'Residential Townhouse',
        price: 95000000,
        image: 'https://via.placeholder.com/300x200?text=Town+6',
        beds: 3,
        baths: 2,
      },
      {
        id: 7,
        title: 'Investment Property',
        price: 75000000,
        image: 'https://via.placeholder.com/300x200?text=Invest+7',
        beds: 2,
        baths: 2,
      },
      {
        id: 8,
        title: 'Premium Duplex',
        price: 180000000,
        image: 'https://via.placeholder.com/300x200?text=Duplex+8',
        beds: 4,
        baths: 3,
      },
      {
        id: 9,
        title: 'Waterfront Mansion',
        price: 250000000,
        image: 'https://via.placeholder.com/300x200?text=Mansion+9',
        beds: 6,
        baths: 5,
      },
    ],
    sellerReviews: [
      {
        id: 1,
        author: 'Ade Okafor',
        rating: 5,
        date: '2 weeks ago',
        comment: 'Excellent service!',
      },
    ],
  };

  const data = { ...defaultSeller, ...seller };
  // Always use the caller-supplied properties if provided (even if empty array)
  if (seller && 'properties' in seller) {
    data.properties = seller.properties;
  }
  const authToken = localStorage.getItem('naijanest_token');
  const isAuthenticated = Boolean(authToken);
  const isProfileOwner = Boolean(user?.id && String(user.id) === String(data.id));
  const phoneValue = data.contact?.phone;
  const emailValue = data.contact?.email;
  const hasPhone = Boolean(phoneValue && phoneValue !== 'Not provided');
  const hasEmail = Boolean(emailValue && emailValue !== 'Not provided');
  const canMessageSeller = data.canMessage !== false;

  const getPropertyImage = (property) => {
    if (property.image) {
      return property.image;
    }
    if (Array.isArray(property.images) && property.images.length > 0) {
      return property.images[0];
    }
    return 'data:image/svg+xml,%3Csvg xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22 width%3D%22300%22 height%3D%22200%22%3E%3Crect width%3D%22300%22 height%3D%22200%22 fill%3D%22%23e8e8e8%22%2F%3E%3Ctext x%3D%2250%25%22 y%3D%2250%25%22 dominant-baseline%3D%22middle%22 text-anchor%3D%22middle%22 font-family%3D%22sans-serif%22 font-size%3D%2214%22 fill%3D%22%23999%22%3ENo Image%3C%2Ftext%3E%3C%2Fsvg%3E';
  };

  // Pagination calculation
  const allProperties = data.properties || [];
  const totalPropertyCount = Number(data.stats?.propertiesListed ?? allProperties.length) || 0;
  const totalPages = Math.ceil(allProperties.length / propertiesPerPage);
  const startIndex = (currentPage - 1) * propertiesPerPage;
  const paginatedProperties = allProperties.slice(startIndex, startIndex + propertiesPerPage);
  const safeTotalPages = Math.max(totalPages, 1);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, allProperties.length]);

  useEffect(() => {
    setSellerReviews(seller.sellerReviews || seller.reviews || []);
  }, [seller]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const openMessageFlag = params.get('openMessage');
    const conversationId = params.get('conversationId');

    if (openMessageFlag !== '1') {
      return;
    }

    if (!canMessageSeller) {
      addToast('This seller has disabled direct messaging.', 'error');
      return;
    }

    if (!isAuthenticated) {
      addToast('Please log in to send a message.', 'error');
      navigate('/login');
      return;
    }

    const nextParams = new URLSearchParams();
    nextParams.set('sellerId', String(data.id));
    nextParams.set('sellerName', data.name || 'Seller');
    nextParams.set('source', 'seller-profile');
    const propertyId = params.get('propertyId');
    const propertyTitle = params.get('propertyTitle');
    const propertyPrice = params.get('propertyPrice');
    if (propertyId) {
      nextParams.set('propertyId', propertyId);
    }
    if (propertyTitle) {
      nextParams.set('propertyTitle', propertyTitle);
    }
    if (propertyPrice) {
      nextParams.set('propertyPrice', propertyPrice);
    }
    if (conversationId) {
      nextParams.set('conversationId', conversationId);
    }

    navigate(`/messages?${nextParams.toString()}`);
  }, [location.search, navigate, canMessageSeller, isAuthenticated]);

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handlePropertyClick = (propertyId) => {
    const nextParams = new URLSearchParams();
    nextParams.set('sellerId', String(data.id));
    nextParams.set('sellerName', data.name || 'Seller');
    nextParams.set('source', 'seller-profile');
    navigate(`/property/${propertyId}?${nextParams.toString()}`);
  };

  const handlePropertyKeyDown = (event, propertyId) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handlePropertyClick(propertyId);
    }
  };

  const handleMessageClick = () => {
    if (!canMessageSeller) {
      addToast('This seller has disabled direct messaging.', 'error');
      return;
    }

    if (!isAuthenticated) {
      addToast('Please log in to send a message.', 'error');
      navigate('/login');
      return;
    }

    trackEvent('contact_agent_click', { seller_id: data.id, method: 'message' });

    const nextParams = new URLSearchParams();
    nextParams.set('sellerId', String(data.id));
    nextParams.set('sellerName', data.name || 'Seller');
    nextParams.set('source', 'seller-profile');
    appendPropertyContext(nextParams);
    navigate(`/messages?${nextParams.toString()}`);
  };

  const handleReportClick = () => {
    if (reportSubmitted) {
      return;
    }

    if (!isAuthenticated) {
      addToast('Please log in to report this seller.', 'error');
      navigate('/login');
      return;
    }

    setShowReportModal(true);
  };

  const handleSubmitReviewResponse = async (reviewId) => {
    const responseText = (responseDrafts[reviewId] || '').trim();
    if (!responseText) {
      addToast('Enter a response before saving.', 'error');
      return;
    }

    setResponseSubmittingId(reviewId);

    try {
      const response = await authedFetch(`${API_BASE}/sellers/${data.id}/reviews/${reviewId}/response`, {
        method: 'POST',
        body: JSON.stringify({ response: responseText }),
      });
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to save response.');
      }

      setSellerReviews((current) =>
        current.map((review) =>
          review.id === reviewId
            ? {
              ...review,
              seller_response: result.seller_response,
              seller_response_at: result.seller_response_at,
            }
            : review,
        ),
      );
      setResponseDrafts((current) => ({ ...current, [reviewId]: '' }));
      setRespondingReviewId(null);
      addToast('Review response saved.', 'success');
    } catch (error) {
      addToast(error.message, 'error');
    } finally {
      setResponseSubmittingId(null);
    }
  };

  const handleToggleReviewHelpful = async (reviewId) => {
    if (!user || helpfulPendingReviewId) {
      return;
    }
    setHelpfulPendingReviewId(reviewId);
    try {
      const response = await authedFetch(`${API_BASE}/sellers/reviews/${reviewId}/helpful`, {
        method: 'POST',
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || 'Failed to save vote.');
      }
      setSellerReviews((current) =>
        current.map((review) =>
          review.id === reviewId
            ? {
              ...review,
              helpful_count: result.helpful_count,
              current_user_helpful_voted: result.current_user_helpful_voted,
            }
            : review,
        ),
      );
    } catch (error) {
      addToast(error.message || 'Failed to save vote.', 'error');
    } finally {
      setHelpfulPendingReviewId(null);
    }
  };

  const openReviewReportModal = (review) => {
    setReviewReportStatus('');
    setReviewReportDetails('');
    setReviewReportReason('spam');
    setReportingReview(review);
  };

  const handleSubmitReviewReport = async (event) => {
    event.preventDefault();
    if (!reportingReview) {
      return;
    }
    setReviewReportLoading(true);
    setReviewReportStatus('');
    try {
      const response = await authedFetch(`${API_BASE}/sellers/reviews/${reportingReview.id}/report`, {
        method: 'POST',
        body: JSON.stringify({
          reason: reviewReportReason,
          description: reviewReportDetails.trim(),
        }),
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || 'Failed to submit report.');
      }
      setReviewReportStatus('success');
      setReviewReportDetails('');
    } catch (err) {
      addToast(err.message || 'Failed to submit report.', 'error');
    } finally {
      setReviewReportLoading(false);
    }
  };

  // Flattened across every review, not just one - clicking any review photo
  // opens a single swipeable set of every photo submitted for this seller.
  const allReviewPhotos = sellerReviews.flatMap((review) =>
    (Array.isArray(review.photos) ? review.photos : []).map((photo) => ({
      id: photo.id,
      url: photo.url || photo.thumbnail_url || photo.thumbnailUrl,
    })),
  );

  const handleToggleFavorite = async () => {
    if (!user) {
      navigate('/login', {
        state: { from: `${location.pathname}${location.search || ''}` },
      });
      return;
    }
    if (favoriteSubmitting) {
      return;
    }

    const nextState = !isFavorite;
    setFavoriteSubmitting(true);
    try {
      const response = await authedFetch(`${API_BASE}/sellers/${seller.id}/favorite`, {
        method: nextState ? 'POST' : 'DELETE',
      });
      if (!response.ok) {
        throw new Error('Failed to update saved sellers.');
      }
      setIsFavorite(nextState);
      setSavedCount((current) => Math.max(0, current + (nextState ? 1 : -1)));
    } catch (error) {
      addToast(error.message || 'Failed to update saved sellers.', 'error');
    } finally {
      setFavoriteSubmitting(false);
    }
  };

  const handleSubmitReport = async () => {
    if (reportSubmitting) {
      return;
    }

    setReportSubmitting(true);

    try {
      const response = await fetch(`${API_BASE}/sellers/${data.id}/report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        body: JSON.stringify({
          reason: reportReason,
          description: reportDescription.trim(),
        }),
      });

      if (!response.ok) {
        throw new Error('Unable to submit report. Please try again.');
      }

      setShowReportModal(false);
      setReportSubmitted(true);
      setReportDescription('');
      addToast('Report submitted successfully.', 'success');
    } catch (error) {
      addToast(error.message, 'error');
    } finally {
      setReportSubmitting(false);
    }
  };

  const renderPaginationControls = (position = 'bottom') => {
    if (allProperties.length === 0) {
      return null;
    }

    return (
      <div className={`pagination-controls pagination-controls-${position}`}>
        <button className="pagination-btn" onClick={handlePrevPage} disabled={currentPage === 1}>
          ← Previous
        </button>
        <span className="pagination-info">
          Page {currentPage} of {safeTotalPages}
        </span>
        <span className="pagination-count-info">
          Showing {paginatedProperties.length} of {allProperties.length} properties
        </span>
        <button
          className="pagination-btn"
          onClick={handleNextPage}
          disabled={currentPage === safeTotalPages}
        >
          Next →
        </button>
      </div>
    );
  };

  // Generate initials from seller name for fallback avatar
  const getInitials = (name) => {
    if (!name) {
      return '?';
    }
    const names = name.split(' ');
    if (names.length > 1) {
      return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const initials = getInitials(data.name);

  return (
    <div className="seller-profile-simple">
      {/* Simple Header */}
      <div className="seller-header-simple">
        <div className="seller-header-top">
          <div className="seller-avatar-info">
            {data.avatar ? (
              <>
                <img
                  src={data.avatar}
                  alt={data.name}
                  className="seller-avatar-simple"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    // Show fallback if it exists
                    const fallback =
                      e.target.parentElement.querySelector('.seller-avatar-fallback');
                    if (fallback) {
                      fallback.style.display = 'flex';
                    }
                  }}
                />
                <div className="seller-avatar-fallback" style={{ display: 'none' }}>
                  <span className="avatar-initials">{initials}</span>
                </div>
              </>
            ) : null}
            {!data.avatar && (
              <div className="seller-avatar-fallback">
                <span className="avatar-initials">{initials}</span>
              </div>
            )}
            <div className="seller-info-text">
              <div className="seller-name-row">
                <h1 className="seller-name-simple">{data.name}</h1>
                {data.verified && <CheckCircle className="verified-icon-simple" size={20} />}
              </div>
              {data.contributorProfile &&
                (data.contributorProfile.professional_role || data.contributorProfile.organisation) && (
                <p className="seller-role-line">
                  {[data.contributorProfile.professional_role, data.contributorProfile.organisation]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              )}
              <div className="seller-rating-row">
                <div className="rating-display">
                  <Star size={16} className="star-filled" />
                  <span className="rating-text">
                    {data.rating} ({data.reviewCount} reviews)
                  </span>
                </div>
              </div>
              <p className="seller-location">{data.location}</p>
              <p className="seller-joindate">{data.joinDate}</p>
              <div className="seller-contact-inline">
                {hasPhone && (
                  <span className="seller-contact-inline-item">
                    <span className="seller-contact-label">Phone:</span> {phoneValue}
                  </span>
                )}
                {hasEmail && (
                  <span className="seller-contact-inline-item">
                    <span className="seller-contact-label">Email:</span>{' '}
                    <a href={`mailto:${emailValue}`} className="seller-contact-link">
                      {emailValue}
                    </a>
                  </span>
                )}
                {!hasPhone && !hasEmail && (
                  <span className="seller-contact-inline-empty">No public contact</span>
                )}
              </div>
              {(data.social?.instagram ||
                data.social?.twitter ||
                data.social?.facebook ||
                data.social?.whatsapp) && (
                <div className="seller-social-links">
                  {data.social.instagram && (
                    <a
                      href={`https://instagram.com/${data.social.instagram}`}
                      target="_blank"
                      rel="noreferrer"
                      aria-label="Instagram"
                      className="seller-social-link"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                      </svg>
                    </a>
                  )}
                  {data.social.twitter && (
                    <a
                      href={`https://x.com/${data.social.twitter}`}
                      target="_blank"
                      rel="noreferrer"
                      aria-label="X (Twitter)"
                      className="seller-social-link"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                      </svg>
                    </a>
                  )}
                  {data.social.facebook && (
                    <a
                      href={data.social.facebook}
                      target="_blank"
                      rel="noreferrer"
                      aria-label="Facebook"
                      className="seller-social-link"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                      </svg>
                    </a>
                  )}
                  {data.social.whatsapp && (
                    <a
                      href={`https://wa.me/${data.social.whatsapp.replace(/\D/g, '')}`}
                      target="_blank"
                      rel="noreferrer"
                      aria-label="WhatsApp"
                      className="seller-social-link"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                        <path d="M12.031 0C5.402 0 .022 5.377.02 11.984c0 2.113.554 4.176 1.607 6l-1.71 6.243 6.4-1.677a12.02 12.02 0 005.71 1.454h.005c6.627 0 12.007-5.377 12.01-11.985A11.87 11.87 0 0020.379 3.5 11.92 11.92 0 0012.031 0zm7.02 19.001a9.947 9.947 0 01-7.021 2.912h-.004a9.95 9.95 0 01-5.062-1.387l-.363-.216-3.798.996 1.014-3.7-.237-.38a9.933 9.933 0 01-1.526-5.302c0-5.489 4.474-9.955 9.973-9.955a9.9 9.9 0 017.048 2.916 9.884 9.884 0 012.917 7.037c-.002 5.489-4.476 9.955-9.941 9.955z" />
                      </svg>
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="seller-actions-simple">
            {!isProfileOwner && (
              <button
                type="button"
                className={`action-btn-simple ${isFavorite ? 'favorite-active' : ''}`}
                onClick={handleToggleFavorite}
                disabled={favoriteSubmitting}
                aria-pressed={isFavorite}
              >
                <Heart size={20} fill={isFavorite ? 'currentColor' : 'none'} />
                <span>{isFavorite ? 'Saved' : 'Save'}</span>
              </button>
            )}

            <button
              type="button"
              className="action-btn-simple"
              onClick={handleMessageClick}
              disabled={!canMessageSeller}
            >
              <MessageSquare size={20} />
              <span>{canMessageSeller ? 'Message' : 'Messaging Off'}</span>
            </button>

            <Tooltip
              content={reportSubmitted ? 'You submitted a report for this seller' : ''}
              position="bottom"
            >
              <button
                type="button"
                className="action-btn-simple"
                onClick={handleReportClick}
                disabled={reportSubmitted}
              >
                <Flag size={20} />
                <span>{reportSubmitted ? 'Reported' : 'Report'}</span>
              </button>
            </Tooltip>
          </div>
        </div>

        <div className="seller-bio-section">
          <p className="seller-bio-text">{data.bio || 'No bio provided yet.'}</p>

          <div className="seller-metrics-compact">
            <div className="seller-metric-item">
              <span className="seller-metric-value">
                {data.stats.propertiesListed} <small>/ {data.stats.sold}</small>
              </span>
              <span className="seller-metric-label">Listed / Sold</span>
            </div>
            <div className="seller-metric-item">
              <span className="seller-metric-value">{data.stats.responseTime}</span>
              <span className="seller-metric-label">Response</span>
            </div>
            <div className="seller-metric-item">
              <span className="seller-metric-value">
                {savedCount}{' '}
                <small>
                  /{' '}
                  {data.recommendPercent === null || data.recommendPercent === undefined
                    ? '—'
                    : `${data.recommendPercent}%`}
                </small>
              </span>
              <span className="seller-metric-label">Saved / Recommend</span>
            </div>
            {data.isContributor && (
              <div className="seller-metric-item">
                <span className="seller-metric-value">{data.articles?.length || 0}</span>
                <span className="seller-metric-label">Articles</span>
              </div>
            )}
          </div>
        </div>

        {showReportModal && (
          <div
            className="modal-backdrop"
            role="dialog"
            aria-modal="true"
            aria-labelledby="report-modal-title"
          >
            <div className="modal-card">
              <h2 id="report-modal-title">Report Seller</h2>
              <p>Please confirm this report and provide details for moderation review.</p>

              <label className="modal-field-label" htmlFor="report-reason">
                Reason
              </label>
              <select
                id="report-reason"
                className="modal-select"
                value={reportReason}
                onChange={(event) => setReportReason(event.target.value)}
              >
                <option>Suspicious activity</option>
                <option>Spam or scam</option>
                <option>Inappropriate behavior</option>
                <option>Fake listings</option>
              </select>

              <label className="modal-field-label" htmlFor="report-description">
                Description
              </label>
              <textarea
                id="report-description"
                className="modal-textarea"
                value={reportDescription}
                onChange={(event) => setReportDescription(event.target.value)}
                rows={4}
                placeholder="Share what happened (optional)"
              />

              <div className="modal-actions">
                <button
                  type="button"
                  className="modal-btn secondary"
                  onClick={() => setShowReportModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="modal-btn danger"
                  onClick={handleSubmitReport}
                  disabled={reportSubmitting}
                >
                  {reportSubmitting ? 'Submitting...' : 'Confirm Report'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Properties Tab */}
      {activeTab === 'properties' && (
        <div className="properties-section">
          <div className="content-head">
            <div className="tabs-simple tabs-inline">
              <button
                className={`tab-btn ${activeTab === 'properties' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('properties');
                }}
              >
                Properties ({totalPropertyCount})
              </button>
              {data.isContributor && (
                <button
                  className={`tab-btn ${activeTab === 'articles' ? 'active' : ''}`}
                  onClick={() => setActiveTab('articles')}
                >
                  Articles ({data.articles?.length || 0})
                </button>
              )}
              <button
                className={`tab-btn ${activeTab === 'reviews' ? 'active' : ''}`}
                onClick={() => setActiveTab('reviews')}
              >
                Reviews ({sellerReviews.length || 0})
              </button>
            </div>
            {allProperties.length > 0 && renderPaginationControls('top')}
          </div>

          {allProperties.length === 0 ? (
            <div className="no-properties-message">
              <p>No properties listed yet</p>
            </div>
          ) : (
            <>
              <div className="properties-list">
                {paginatedProperties.map((property) => (
                  <div
                    key={property.id}
                    className="property-row"
                    onClick={() => handlePropertyClick(property.id)}
                    onKeyDown={(event) => handlePropertyKeyDown(event, property.id)}
                    role="button"
                    tabIndex={0}
                    aria-label={`View ${property.title}`}
                  >
                    <div className="property-image-container">
                      <img
                        src={getPropertyImage(property)}
                        alt={property.title}
                        className="property-image"
                        onError={(e) =>
                          (e.target.src =
                            'data:image/svg+xml,%3Csvg xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22 width%3D%22300%22 height%3D%22200%22%3E%3Crect width%3D%22300%22 height%3D%22200%22 fill%3D%22%23e8e8e8%22%2F%3E%3Ctext x%3D%2250%25%22 y%3D%2250%25%22 dominant-baseline%3D%22middle%22 text-anchor%3D%22middle%22 font-family%3D%22sans-serif%22 font-size%3D%2214%22 fill%3D%22%23999%22%3ENo Image%3C%2Ftext%3E%3C%2Fsvg%3E')
                        }
                      />
                    </div>
                    <div className="property-info">
                      <h3 className="property-title">{property.title}</h3>
                      {(data.business_logo_url || data.company_name) && (
                        <div className="listing-card__agent-badge">
                          {data.business_logo_url && (
                            <img
                              src={data.business_logo_url}
                              alt=""
                              className="listing-card__agent-logo"
                            />
                          )}
                          {data.company_name && (
                            <span className="listing-card__agent-name">{data.company_name}</span>
                          )}
                        </div>
                      )}
                      <p className="property-price">{formatPrice(property.price || 0, currencyCtx)}</p>
                      {(property.area || property.state || property.location) && (
                        <p className="property-location">
                          {property.area && property.state
                            ? `${property.area}, ${property.state}`
                            : property.location || property.area || property.state}
                        </p>
                      )}
                      <div className="property-specs">
                        {property.beds > 0 && (
                          <span>
                            🛏️ {property.beds} bed{property.beds > 1 ? 's' : ''}
                          </span>
                        )}
                        {property.baths > 0 && (
                          <span>
                            🚿 {property.baths} bath{property.baths > 1 ? 's' : ''}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {renderPaginationControls('bottom')}
            </>
          )}
        </div>
      )}

      {/* Reviews Tab */}
      {activeTab === 'reviews' && (
        <div className="reviews-section property-reviews-section">
          <div className="content-head">
            <div className="tabs-simple tabs-inline">
              <button
                className={`tab-btn ${activeTab === 'properties' ? 'active' : ''}`}
                onClick={() => setActiveTab('properties')}
              >
                Properties ({totalPropertyCount})
              </button>
              {data.isContributor && (
                <button
                  className={`tab-btn ${activeTab === 'articles' ? 'active' : ''}`}
                  onClick={() => setActiveTab('articles')}
                >
                  Articles ({data.articles?.length || 0})
                </button>
              )}
              <button
                className={`tab-btn ${activeTab === 'reviews' ? 'active' : ''}`}
                onClick={() => setActiveTab('reviews')}
              >
                Reviews ({data.sellerReviews?.length || 0})
              </button>
            </div>
          </div>

          {isProfileOwner && (
            <p className="subtext">You can respond to reviews on your profile.</p>
          )}

          <div className="reviews-list">
            {sellerReviews.slice(0, visibleReviewCount).map((review) => {
              const isAnon = review.is_anonymous === true || Number(review.is_anonymous) === 1;
              const reviewerName = review.author || review.buyer_name;
              return (
                <div key={review.id} className="review-item">
                  <div className="review-header">
                    <div
                      className={`review-avatar ${isAnon ? 'review-avatar--anon' : 'review-avatar--named'}`}
                      aria-hidden="true"
                    >
                      {isAnon ? (
                        <UserIcon size={13} />
                      ) : review.buyer_avatar ? (
                        <img src={review.buyer_avatar} alt="" />
                      ) : (
                        getInitials(reviewerName)
                      )}
                    </div>
                    <div className="review-header-text">
                      <h4 className="reviewer-name">{reviewerName}</h4>
                      <span className="review-date">
                        {review.date ||
                          (review.created_at
                            ? new Date(review.created_at).toLocaleDateString()
                            : 'Recently')}
                        {review.is_edited && (
                          <span className="review-edited-tag" title="This review was edited">
                            {' '}(edited)
                          </span>
                        )}
                      </span>
                    </div>
                  </div>
                  {review.property_title && <p className="subtext">{review.property_title}</p>}
                  <div className="review-rating">
                    {[...Array(5)].map((_, i) => (
                      <span key={i} className="review-star">
                        {i < Number(review.rating || 0) ? '⭐' : '☆'}
                      </span>
                    ))}
                  </div>
                  {review.title && <p className="review-title">{review.title}</p>}
                  <p className="review-comment">{review.comment}</p>
                  {Array.isArray(review.review_type) && review.review_type.length > 0 && (
                    <div className="review-type-tags">
                      {review.review_type.map((key) => (
                        <span key={key} className="review-type-tag">
                          {REVIEW_TYPE_OPTIONS.find((option) => option.key === key)?.label || key}
                        </span>
                      ))}
                    </div>
                  )}
                  {Array.isArray(review.photos) && review.photos.length > 0 && (
                    <div className="review-photo-strip">
                      {review.photos.map((photo) => (
                        <img
                          key={photo.id || photo.url}
                          src={photo.thumbnail_url || photo.thumbnailUrl || photo.url}
                          alt=""
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            const index = allReviewPhotos.findIndex((p) => p.id === photo.id);
                            setLightboxIndex(index >= 0 ? index : 0);
                          }}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              const index = allReviewPhotos.findIndex((p) => p.id === photo.id);
                              setLightboxIndex(index >= 0 ? index : 0);
                            }
                          }}
                        />
                      ))}
                    </div>
                  )}
                  {review.did_visit === true && (
                    <div className="review-visited-pill">✓ Visited in person</div>
                  )}
                  {review.recommendation_choice && (
                    <div className="review-recommend-line">
                      Would recommend:{' '}
                      <strong>
                        {review.recommendation_choice === 'yes'
                          ? 'Yes'
                          : review.recommendation_choice === 'no'
                            ? 'No'
                            : 'Not sure'}
                      </strong>
                    </div>
                  )}
                  <div className="review-actions">
                    <button
                      type="button"
                      className={`review-action-btn${review.current_user_helpful_voted ? ' is-active' : ''}`}
                      onClick={() => handleToggleReviewHelpful(review.id)}
                      disabled={!user || helpfulPendingReviewId === review.id}
                    >
                      <ThumbsUpIcon size={11} />
                      Helpful{review.helpful_count > 0 ? ` · ${review.helpful_count}` : ''}
                    </button>
                    <span className="review-action-divider" />
                    <button
                      type="button"
                      className="review-action-btn review-action-btn--report"
                      onClick={() => openReviewReportModal(review)}
                      disabled={!user}
                    >
                      <AlertCircleIcon size={11} />
                      Report
                    </button>
                    {isProfileOwner && !review.seller_response && (
                      <>
                        <span className="review-action-divider" />
                        <button
                          type="button"
                          className="review-action-btn review-action-btn--respond"
                          onClick={() =>
                            setRespondingReviewId((current) => (current === review.id ? null : review.id))
                          }
                        >
                          <ReplyIcon size={11} />
                          Respond
                        </button>
                      </>
                    )}
                  </div>
                  {review.seller_response && (
                    <div className="review-response">
                      <div className="review-response-avatar" aria-hidden="true">
                        {getInitials(data.company_name || data.name)}
                      </div>
                      <div className="review-response-body">
                        <div className="review-response-head">
                          <strong>Response from {data.company_name || data.name || 'the seller'}</strong>
                          {review.seller_response_at && (
                            <span className="review-response-date">
                              {new Date(review.seller_response_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                        <p className="review-response-text">{review.seller_response}</p>
                      </div>
                    </div>
                  )}
                  {isProfileOwner && !review.seller_response && respondingReviewId === review.id && (
                    <div className="review-response-form">
                      <div className="review-response-avatar" aria-hidden="true">
                        {getInitials(data.company_name || data.name)}
                      </div>
                      <div className="review-response-form-body">
                        <p className="review-response-form-label">
                          Respond as {data.company_name || data.name || 'the seller'}
                        </p>
                        <textarea
                          rows="2"
                          placeholder="Write a reply to this review…"
                          value={responseDrafts[review.id] || ''}
                          onChange={(event) =>
                            setResponseDrafts((current) => ({
                              ...current,
                              [review.id]: event.target.value,
                            }))
                          }
                        />
                        <div className="review-response-form-actions">
                          <button
                            type="button"
                            className="rf-btn-ghost"
                            onClick={() => setRespondingReviewId(null)}
                            disabled={responseSubmittingId === review.id}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="rf-btn-primary"
                            onClick={() => handleSubmitReviewResponse(review.id)}
                            disabled={
                              responseSubmittingId === review.id ||
                              !String(responseDrafts[review.id] || '').trim()
                            }
                          >
                            {responseSubmittingId === review.id ? 'Posting…' : 'Post reply'}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {sellerReviews.length > visibleReviewCount && (
            <button
              type="button"
              className="property-detail-more-btn"
              onClick={() => setVisibleReviewCount(sellerReviews.length)}
            >
              Load {sellerReviews.length - visibleReviewCount} more review
              {sellerReviews.length - visibleReviewCount === 1 ? '' : 's'} ↓
            </button>
          )}
        </div>
      )}

      {/* Articles Tab - only reachable via the button above, which only
          renders when data.isContributor is true. */}
      {activeTab === 'articles' && (
        <div className="properties-section">
          <div className="content-head">
            <div className="tabs-simple tabs-inline">
              <button
                className={`tab-btn ${activeTab === 'properties' ? 'active' : ''}`}
                onClick={() => setActiveTab('properties')}
              >
                Properties ({totalPropertyCount})
              </button>
              <button className="tab-btn active" onClick={() => setActiveTab('articles')}>
                Articles ({data.articles?.length || 0})
              </button>
              <button
                className={`tab-btn ${activeTab === 'reviews' ? 'active' : ''}`}
                onClick={() => setActiveTab('reviews')}
              >
                Reviews ({sellerReviews.length || 0})
              </button>
            </div>
          </div>

          {(data.articles || []).length === 0 ? (
            <div className="no-properties-message">
              <p>No published articles yet</p>
            </div>
          ) : (
            <div className="properties-list">
              {data.articles.map((article) => (
                <div
                  key={article.slug}
                  className="property-row"
                  onClick={() => navigate(`/blog/${article.slug}`)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') navigate(`/blog/${article.slug}`);
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={`Read ${article.title}`}
                >
                  <div className="property-image-container">
                    {article.cover_image_url && (
                      <img src={article.cover_image_url} alt={article.title} className="property-image" />
                    )}
                  </div>
                  <div className="property-info">
                    <h3 className="property-title">{article.title}</h3>
                    <p className="property-location">
                      {article.category ? blogCategoryLabel(article.category) : ''}
                      {article.category && (article.approved_at || article.created_at) ? ' · ' : ''}
                      {(article.approved_at || article.created_at) &&
                        new Date(article.approved_at || article.created_at).toLocaleDateString('en-NG')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <ReviewPhotoLightbox
        isOpen={lightboxIndex !== null}
        images={allReviewPhotos.map((photo) => photo.url)}
        activeIndex={lightboxIndex || 0}
        onActiveIndexChange={(updater) =>
          setLightboxIndex((current) => (typeof updater === 'function' ? updater(current ?? 0) : updater))
        }
        onClose={() => setLightboxIndex(null)}
      />

      <Modal
        isOpen={reportingReview !== null}
        onClose={() => setReportingReview(null)}
        title="Report this review"
      >
        {reviewReportStatus === 'success' ? (
          <>
            <Alert type="success">
              Thank you, your report has been submitted and will be reviewed by our team.
            </Alert>
            <div className="property-report-actions">
              <Button variant="secondary" onClick={() => setReportingReview(null)}>
                Close
              </Button>
            </div>
          </>
        ) : (
          <form onSubmit={handleSubmitReviewReport} className="property-report-form">
            <label className="property-report-label" htmlFor="seller-review-report-reason">
              Reason for reporting
            </label>
            <Select
              id="seller-review-report-reason"
              value={reviewReportReason}
              onChange={(e) => setReviewReportReason(e.target.value)}
              options={[
                { value: 'spam', label: 'Spam' },
                { value: 'inappropriate', label: 'Inappropriate content' },
                { value: 'fake', label: 'Fake review' },
                { value: 'off_topic', label: 'Off-topic / not about this seller' },
                { value: 'other', label: 'Other' },
              ]}
            />
            <label className="property-report-label" htmlFor="seller-review-report-details">
              Additional details <span className="admin-muted">(optional)</span>
            </label>
            <textarea
              id="seller-review-report-details"
              className="property-report-textarea"
              rows={4}
              maxLength={500}
              placeholder="Describe the issue…"
              value={reviewReportDetails}
              onChange={(e) => setReviewReportDetails(e.target.value)}
            />
            <div className="property-report-actions">
              <Button type="submit" variant="primary" disabled={reviewReportLoading}>
                {reviewReportLoading ? 'Submitting…' : 'Submit report'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                disabled={reviewReportLoading}
                onClick={() => setReportingReview(null)}
              >
                Cancel
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default SellerProfileFull;
